import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Circle, Copy, FileText, Link2, Loader2, MessageCircle, Scale } from 'lucide-react';
import { api } from '../../lib/api';
import { abrirPestanaYa, llevarPestanaA, mensajeCita } from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Títulos de los pasos: los mismos que ve el paciente en su guía y que
 * enumera el WhatsApp (2026-09-03; revisión: igual que la página). */
const TITULOS = {
  instrucciones: 'Instrucciones preoperatorias',
  preoperatorio: 'Aportación de pruebas preoperatorias',
  biometria: 'Datos para la anestesia',
  consentimientos: 'Lectura de consentimientos informados',
  postoperatorio: 'Cuidados postoperatorios',
  pago: 'Información para realizar el pago',
};
/** Orden de los seis pasos de una cirugía. Un retoque llega con los cinco
 * suyos en `pasos_orden` (dirección, 2026-09-08, punto 8: pruebas y cuidados
 * son un mensaje, los consentimientos cierran y no hay paso de pago). */
const ORDEN_CIRUGIA = ['instrucciones', 'preoperatorio', 'biometria', 'consentimientos', 'postoperatorio', 'pago'];

/**
 * El número de WhatsApp que el SERVIDOR ha decidido para este paciente,
 * sacado del `wa.me` que él mismo devuelve. Aquí NO se compone un teléfono:
 * la regla de prefijos vive en services/telefonos.py y copiarla en la
 * pantalla es exactamente la avería del 2026-09-17 (un móvil portugués de
 * nueve cifras convertido en español: el mensaje se le iba a un desconocido).
 * Si el servidor no da número, esto devuelve '' y no se manda nada.
 */
const numeroDeWhatsapp = (url) => (String(url || '').match(/^https:\/\/wa\.me\/(\+?\d+)/) || [])[1] || '';

/**
 * El WhatsApp de «Enviar la guía»: corto y con SOLO el enlace. No repite las
 * pautas (el recordatorio del retoque ya las lleva desde el 2026-09-17 y no
 * se toca) ni enumera apartados. Va sin nombre y sin saludo a propósito: el
 * «Estimado/Estimada» lo decide el servidor por el género de la ficha
 * (services/genero.py) y aquí no se reinventa.
 */
const textoGuia = (url) => (
  'Le dejamos el enlace a su guía de pasos previos a la intervención, '
  + `para que pueda ir completándola desde el móvil:\n${url}`
);

function Fila({ n, titulo, hecho, detalle }) {
  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-border last:border-0">
      <span className={`shrink-0 mt-0.5 w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold ${
        hecho ? 'bg-emerald-600 text-white' : 'bg-muted text-muted-foreground'
      }`}>
        {hecho ? <Check className="w-3.5 h-3.5" /> : n}
      </span>
      <div className="min-w-0 flex-1">
        <div className={`text-sm ${hecho ? 'text-[#13231E] font-medium' : 'text-muted-foreground'}`}>
          {titulo}
        </div>
        {detalle && <div className="text-xs text-muted-foreground mt-0.5">{detalle}</div>}
      </div>
      <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide ${
        hecho ? 'text-emerald-700' : 'text-amber-700'
      }`}>
        {hecho ? 'Hecho' : 'Pendiente'}
      </span>
    </div>
  );
}

/**
 * Cómo va el paciente con sus pasos antes de la cirugía (2026-08-05).
 * Es una vista de SEGUIMIENTO para la clínica: si ya subió la analítica y si
 * dio su peso. Desde aquí se le manda o se copia el enlace, pero eso es lo
 * secundario — lo que hacía falta era ver cómo va.
 */
export default function PasosCirugiaDialog({ open, onOpenChange, cita }) {
  const [datos, setDatos] = useState(null);
  // La consulta CAÍDA aparte del dato (21/9/2026). Ver `cargar`.
  const [error, setError] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviandoGuia, setEnviandoGuia] = useState(false);

  /**
   * El proceso de ESTA cita (dirección, 2026-09-08): cada cirugía tiene su
   * propio enlace; el de una cirugía anterior del paciente no vale aquí.
   *
   * UNA CONSULTA CAÍDA NO ES «NO TIENE ENLACE» (21/9/2026). Antes, el
   * `.catch` dejaba `datos` en null y con null se pintaba el MISMO bloque
   * que cuando el servidor contesta de verdad {tiene_enlace:false}: «Para
   * esta cirugía todavía no se le ha generado el enlace». Recepción mira
   * esto un rato antes de la cirugía: leía que la paciente no tiene enlace,
   * daba por hecho que no había completado nada y se ponía a pedirle por
   * teléfono la analítica que ya había subido. El hermano de este mismo
   * flujo (PasosResumen, en CitaDetailPopover) ya distinguía los tres casos;
   * este diálogo se había quedado fuera.
   */
  const cargar = useCallback(() => {
    if (!cita?.patient_id) return;
    setCargando(true);
    setError(false);
    api.get('/api/cirugia-pasos/estado', { params: { patient_id: cita.patient_id, cita_id: cita.id } })
      .then((r) => { setDatos(r.data); setError(false); })
      .catch(() => { setDatos(null); setError(true); })
      .finally(() => setCargando(false));
  }, [cita?.patient_id, cita?.id]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  const urlPaciente = datos?.token ? `${window.location.origin}/mis-pasos/${datos.token}` : '';

  const crearEnlace = async () => {
    setGenerando(true);
    try {
      await api.post('/api/cirugia-pasos/link', { patient_id: cita.patient_id, cita_id: cita.id });
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo generar el enlace');
    } finally {
      setGenerando(false);
    }
  };

  // Reenviar: EL MISMO recordatorio que el botón Recordatorio del resumen y
  // el panel del día (texto dictado por dirección, punto 8). Antes componía
  // aquí un mensaje propio en tuteo, distinto del dictado: un solo texto para
  // el paciente (revisión 2026-09-03). Ojo con lo que sale por aquí: en una
  // cirugía es la guía de «mis pasos» con su enlace, pero en un RETOQUE son
  // las pautas preoperatorias, sin guía y sin enlace (administración, 2026-09-17).
  // Por eso el rótulo del botón se ramifica, y por eso existe «Enviar la
  // guía» debajo.
  const mandarWhatsapp = async () => {
    const ventana = abrirPestanaYa();
    setEnviando(true);
    try {
      const d = await mensajeCita(cita.id, 'recordatorio');
      if (!d?.whatsapp_url) {
        ventana?.close();
        toast.error('El paciente no tiene un teléfono válido para WhatsApp');
        return;
      }
      llevarPestanaA(ventana, d.whatsapp_url);
    } catch (e) {
      ventana?.close();
      toast.error(e?.response?.data?.detail || 'No se pudo preparar el mensaje');
    } finally {
      setEnviando(false);
    }
  };

  /**
   * «Enviar la guía»: WhatsApp corto con SOLO el enlace, A MANO.
   *
   * Por qué hace falta (2026-09-17): al quitar la guía del recordatorio de
   * retoque se fue con ella la creación del enlace, y el paciente de retoque
   * se quedó sin ninguna vía automática de recibirlo —ni por el recordatorio
   * ni por la confirmación—. Por ese enlace es por donde firma sus
   * consentimientos desde casa y por donde da peso y altura para el
   * anestesista, así que recepción necesita poder mandárselo cuando haga
   * falta. Esto NO devuelve el envío automático (si el retoque debe llevar
   * guía lo decide dirección): es un botón que se pulsa, no toca el
   * recordatorio y NO marca la cita como recordada.
   *
   * El teléfono no se compone aquí (ver `numeroDeWhatsapp`): se le pide al
   * servidor el WhatsApp del recordatorio de ESTA cita y se le toma el
   * número que él ha decidido, tirando su texto. En un retoque esa consulta
   * no crea nada —el recordatorio de retoque ya no llama a `crear_link`—, y
   * el enlace que se manda es el que este diálogo ya tiene delante.
   */
  const enviarGuia = async () => {
    const ventana = abrirPestanaYa();
    setEnviandoGuia(true);
    try {
      const d = await mensajeCita(cita.id, 'recordatorio');
      const numero = numeroDeWhatsapp(d?.whatsapp_url);
      if (!numero) {
        ventana?.close();
        toast.error('El paciente no tiene un teléfono válido para WhatsApp');
        return;
      }
      llevarPestanaA(ventana, `https://wa.me/${numero}?text=${encodeURIComponent(textoGuia(urlPaciente))}`);
    } catch (e) {
      ventana?.close();
      toast.error(e?.response?.data?.detail || 'No se pudo preparar el mensaje');
    } finally {
      setEnviandoGuia(false);
    }
  };

  const p = datos?.pasos;
  const pruebas = p?.preoperatorio?.archivos || [];
  const consents = p?.consentimientos;
  // Qué se le enseña en el postoperatorio: con herida segura la pauta
  // normal; si no, Terracortril/Ozonest en rojo con el aviso (2026-09-03).
  const herida = p?.postoperatorio?.tiene_herida;
  const detalleHerida = herida === true
    ? 'Con herida: pauta completa'
    : herida === false
      ? 'Sin herida (blefaroplastia inferior): Terracortril y Ozonest destacados con aviso'
      : 'Cirugía sin identificar: Terracortril y Ozonest destacados con aviso';
  // Variante y orden de la guía: los dice el backend (cinco pasos en un
  // retoque, seis en una cirugía), para contar y numerar lo mismo que el
  // paciente (2026-09-08).
  const esRetoque = !!datos?.es_retoque;
  const orden = datos?.pasos_orden?.length ? datos.pasos_orden : ORDEN_CIRUGIA;
  const detalleDe = (clave) => {
    switch (clave) {
      case 'instrucciones':
        return p.instrucciones.disponible ? null : 'Todavía no están preparadas';
      case 'preoperatorio':
        if (esRetoque) {
          return p.preoperatorio.hecho
            ? 'Retoque: no hacen falta pruebas nuevas (mensaje leído)'
            : 'Retoque: no hacen falta pruebas nuevas; le queda leer el mensaje';
        }
        return pruebas.length
          ? `${pruebas.map((a) => a.name).join(' · ')} — en la pestaña Pruebas preoperatorias de la ficha`
          : (p.preoperatorio.visto ? 'La ha visto: dice que aún no la tiene' : 'Sin subir');
      case 'biometria':
        return p.biometria.hecho
          ? `${p.biometria.peso} kg · ${p.biometria.altura} cm`
          : 'Peso y altura: los necesita el anestesista';
      case 'consentimientos':
        return consents.total
          ? `${consents.firmados} de ${consents.total} firmados${
            consents.firmados < consents.total
              ? ` · pendientes: ${consents.detalle.filter((c) => !c.firmado).map((c) => c.nombre).join(', ')}`
              : ''}${consents.visto && !consents.hecho ? ' · ha abierto la firma o los firmará en la clínica' : ''}`
          : 'Sin consentimientos generados para esta cirugía';
      case 'postoperatorio':
        if (esRetoque) {
          return p.postoperatorio.hecho
            ? 'Retoque: los cuidados se explican en la clínica (mensaje leído)'
            : 'Retoque: los cuidados se explican en la clínica; le queda leer el mensaje';
        }
        return p.postoperatorio.disponible ? detalleHerida : 'Todavía no está generado el postoperatorio';
      case 'pago':
        return `Concepto del ingreso: ${p.pago?.concepto || ''}`;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pasos-cirugia-dialog">
        <DialogHeader>
          <DialogTitle>Recordatorio — {cita?.patient_name}</DialogTitle>
        </DialogHeader>

        {cargando ? (
          <div className="py-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          /* No se ha podido consultar: eso se dice, y NO se pinta el bloque
             de «todavía no se le ha generado el enlace», que es un dato
             clínico y aquí sería falso (21/9/2026). */
          <div className="py-6 text-center" data-testid="pasos-error">
            <p className="text-sm text-amber-800 mb-3">
              No se ha podido consultar el seguimiento de esta cirugía.
            </p>
            <Button variant="outline" onClick={cargar} data-testid="pasos-reintentar">
              Volver a intentarlo
            </Button>
          </div>
        ) : datos?.estado === 'cancelada' ? (
          /* Cirugía anulada o «no acude» (22/9/2026): el servidor contesta
             {estado: 'cancelada', motivo} SIN pasos, tenga enlace o no. Sin
             este bloque la rama de abajo hacía `p.instrucciones` con `p`
             undefined y React desmontaba la pantalla entera; y sin enlace se
             ofrecía «Generar sus pasos» para una cirugía que no va a ser.
             Ni filas, ni reenviar, ni generar: la guía está cerrada. */
          <div className="py-6 text-center" data-testid="pasos-cerrada">
            <p className="text-sm text-red-800">
              {datos?.motivo === 'no_acude'
                ? 'No acudió a la cirugía: la guía de mis pasos está cerrada.'
                : 'Cirugía anulada: la guía de mis pasos está cerrada.'}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Si se reprograma, la cita nueva tendrá su propia guía.
            </p>
          </div>
        ) : !datos?.tiene_enlace ? (
          <div className="py-6 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Para esta cirugía todavía no se le ha generado el enlace.
            </p>
            <Button onClick={crearEnlace} disabled={generando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="pasos-generar">
              {generando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Generar sus pasos
            </Button>
          </div>
        ) : (
          <>
            {/* Los pasos de la guía, en el mismo orden y con los mismos
                títulos que ve el paciente (2026-09-03; revisión: igual que
                la página y el WhatsApp). En un retoque, sus cinco (2026-09-08). */}
            {esRetoque && (
              <p className="text-xs text-muted-foreground" data-testid="pasos-retoque">
                Guía de retoque: cinco pasos, sin subida de pruebas, sin pauta y sin paso de pago.
              </p>
            )}
            <div className="rounded-lg border border-border bg-white px-3 py-1">
              {orden.map((clave, i) => (
                <Fila key={clave} n={i + 1} titulo={TITULOS[clave]}
                  hecho={!!p[clave]?.hecho}
                  detalle={detalleDe(clave)} />
              ))}
            </div>

            {pruebas.length > 0 && (
              <div className="mt-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Lo que ha subido</div>
                <div className="flex flex-wrap gap-1.5">
                  {pruebas.map((a) => (
                    <a key={a.id}
                      href={`${BACKEND_URL}/api/public/cirugia-pasos/${datos.token}/prueba/${a.id}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                      data-testid="pasos-ver-prueba"
                    >
                      <FileText className="w-3 h-3 text-[#2E6E5E]" /> {a.name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={mandarWhatsapp} disabled={enviando}
                // Lo que sale por «Reenviar» no es lo mismo en los dos casos
                // desde el 2026-09-17 (administración): en un retoque, las pautas.
                title={esRetoque
                  ? 'Mandarle otra vez el recordatorio de su retoque: las pautas preoperatorias (sin la guía de mis pasos)'
                  : 'Mandarle otra vez el recordatorio de su cirugía: la guía de mis pasos con su enlace'}
                data-testid="pasos-whatsapp">
                {enviando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-1.5" />} Reenviar
              </Button>
              <Button variant="outline" className="flex-1"
                onClick={() => { navigator.clipboard.writeText(urlPaciente); toast.success('Enlace copiado'); }}
                data-testid="pasos-copiar"
              >
                <Copy className="w-4 h-4 mr-1.5" /> Copiar
              </Button>
              <Button variant="ghost" title="Abrir lo que ve el paciente"
                onClick={() => window.open(urlPaciente, '_blank', 'noopener')}
                data-testid="pasos-abrir"
              >
                <Scale className="w-4 h-4" />
              </Button>
            </div>

            {/* Solo en un RETOQUE, y en su propia línea: es la excepción a
                mano, no una acción de todos los días. En una cirugía no se
                pone porque su recordatorio YA manda la guía con el enlace y
                dos botones para lo mismo confunden a quien envía. */}
            {esRetoque && (
              <Button variant="outline" className="w-full" onClick={enviarGuia} disabled={enviandoGuia}
                title="Mandarle un WhatsApp corto con SOLO el enlace de su guía. El recordatorio de un retoque no lo lleva, así que esto se manda a mano cuando haga falta: no queda registrado como recordatorio enviado."
                data-testid="pasos-enviar-guia">
                {enviandoGuia ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Link2 className="w-4 h-4 mr-1.5" />} Enviar la guía
              </Button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
